/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faCog, 
  faSignOutAlt, 
  faUserCircle,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl, makeAuthenticatedRequest } from '../config/api';
import '../styles/AdminHeader.css';

const AdminHeader = () => {
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning':
        return faExclamationTriangle;
      case 'success':
        return faCheckCircle;
      default:
        return faBell;
    }
  };

  const getNotificationClass = (type) => {
    switch (type) {
      case 'warning':
        return 'notification-warning';
      case 'success':
        return 'notification-success';
      default:
        return 'notification-info';
    }
  };

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(
        buildApiUrl('/api/admin/notifications')
      );
      
      if (response?.ok) {
        const data = await response.json();
        if (data.success && data.notifications) {
          setNotifications(data.notifications);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 2 minutes
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
  }, []);

  // Clear all notifications
  const handleClearAll = async () => {
    // Note: This clears from UI only. For full clear, we'd need a backend endpoint
    setNotifications([]);
  };

  return (
    <header className="admin-header">
      <div className="header-left">
        <h1>Super Admin Dashboard</h1>
        <span className="system-status active">System Status: Operational</span>
      </div>
      
      <div className="header-right">
        <div className="notification-container">
          <button 
            className="notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <FontAwesomeIcon icon={faBell} />
            {notifications.length > 0 && (
              <span className="notification-badge">{notifications.length}</span>
            )}
          </button>
          
          {showNotifications && (
            <div className="notifications-dropdown">
              <div className="notifications-header">
                <h3>Notifications</h3>
                {notifications.length > 0 && (
                  <button 
                    className="clear-all"
                    onClick={handleClearAll}
                  >
                    Clear All
                  </button>
                )}
              </div>
              {loading ? (
                <p className="no-notifications">Loading notifications...</p>
              ) : notifications.length === 0 ? (
                <p className="no-notifications">No new notifications</p>
              ) : (
                notifications.map(notification => (
                  <div 
                    key={notification.id} 
                    className={`notification-item ${getNotificationClass(notification.type)}`}
                  >
                    <FontAwesomeIcon icon={getNotificationIcon(notification.type)} />
                    <div className="notification-content">
                      <p>{notification.message}</p>
                      <span className="notification-time">{notification.time || 'Recently'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        
        <div className="admin-controls">
          <button className="settings-btn">
            <FontAwesomeIcon icon={faCog} />
          </button>
          
          <div className="admin-profile">
            <FontAwesomeIcon icon={faUserCircle} className="admin-avatar" />
            <div className="admin-info">
              <span className="admin-name">Admin User</span>
              <span className="admin-role">Super Administrator</span>
            </div>
          </div>
          
          <button className="logout-btn" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
